import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2, Check } from "lucide-react";
import { Reveal } from "@/lib/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const services = [
  "Custom Booth Design & Fabrication",
  "Brand Activation",
  "Rentals & Logistics",
  "Digital / AV / Additional",
  "Not sure yet",
];
const budgets = ["< $25k", "$25k – $75k", "$75k – $150k", "$150k+", "Undecided"];

const initial = {
  name: "",
  email: "",
  company: "",
  phone: "",
  service: "",
  event_date: "",
  budget: "",
  message: "",
};

function Field({ label, name, value, onChange, type = "text", placeholder, testid }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">{label}</span>
      <input
        data-testid={testid}
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="mt-3 w-full border-b border-white/15 bg-transparent pb-3 text-lg font-light text-white outline-none transition-colors placeholder:text-white/25 focus:border-[#FF3B30]"
      />
    </label>
  );
}

export default function QuoteForm() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and a short brief.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      setDone(true);
      toast.success("Brief received. Our team will reach out within one business day.");
      setForm(initial);
      setTimeout(() => setDone(false), 4000);
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="quote" className="mx-auto max-w-[1600px] px-6 py-28 md:px-10 md:py-40">
      <div className="grid grid-cols-1 gap-16 lg:grid-cols-12 lg:gap-24">
        <div className="lg:col-span-5">
          <div className="mb-5 flex items-center gap-3">
            <span className="h-px w-12 bg-[#FF3B30]" />
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-white/50">
              Start a project
            </span>
          </div>
          <Reveal>
            <h2 className="font-display text-4xl font-bold uppercase leading-[0.92] tracking-tighter md:text-6xl lg:text-7xl">
              Let's build<br />your next<br /><span className="text-[#FF3B30]">stand-out.</span>
            </h2>
          </Reveal>
          <p className="mt-8 max-w-sm text-base font-light leading-relaxed text-white/60">
            Tell us about your show, your goals and your timeline. You'll hear
            back from a real strategist — never a bot — within one business day.
          </p>
          <div className="mt-10 space-y-2 font-mono text-sm text-white/50">
            <div>visionsdisplays@gmail.com</div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form onSubmit={submit} data-testid="quote-form" className="grid grid-cols-1 gap-8 md:grid-cols-2">
            <Field label="Full name *" name="name" value={form.name} onChange={onChange} placeholder="Jane Doe" testid="quote-name" />
            <Field label="Work email *" name="email" type="email" value={form.email} onChange={onChange} placeholder="jane@company.com" testid="quote-email" />
            <Field label="Company" name="company" value={form.company} onChange={onChange} placeholder="Acme Inc." testid="quote-company" />
            <Field label="Phone" name="phone" value={form.phone} onChange={onChange} placeholder="+1 555 000 0000" testid="quote-phone" />

            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">Service</span>
              <select
                data-testid="quote-service"
                name="service"
                value={form.service}
                onChange={onChange}
                className="mt-3 w-full border-b border-white/15 bg-transparent pb-3 text-lg font-light text-white outline-none transition-colors focus:border-[#FF3B30]"
              >
                <option value="" className="bg-[#0a0a0a]">Select a service</option>
                {services.map((s) => (
                  <option key={s} value={s} className="bg-[#0a0a0a]">{s}</option>
                ))}
              </select>
            </label>

            <Field label="Event date" name="event_date" type="date" value={form.event_date} onChange={onChange} testid="quote-date" />

            <label className="block md:col-span-2">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">Estimated budget</span>
              <select
                data-testid="quote-budget"
                name="budget"
                value={form.budget}
                onChange={onChange}
                className="mt-3 w-full border-b border-white/15 bg-transparent pb-3 text-lg font-light text-white outline-none transition-colors focus:border-[#FF3B30]"
              >
                <option value="" className="bg-[#0a0a0a]">Select a range</option>
                {budgets.map((b) => (
                  <option key={b} value={b} className="bg-[#0a0a0a]">{b}</option>
                ))}
              </select>
            </label>

            <label className="block md:col-span-2">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">Project brief *</span>
              <textarea
                data-testid="quote-message"
                name="message"
                value={form.message}
                onChange={onChange}
                rows={4}
                placeholder="Booth size, show name, objectives, anything on your mind…"
                className="mt-3 w-full resize-none border-b border-white/15 bg-transparent pb-3 text-lg font-light text-white outline-none transition-colors placeholder:text-white/25 focus:border-[#FF3B30]"
              />
            </label>

            <div className="md:col-span-2">
              <button
                data-testid="quote-submit"
                type="submit"
                disabled={loading || done}
                className="group flex w-full items-center justify-center gap-3 rounded-full bg-white px-8 py-5 font-mono text-xs uppercase tracking-[0.2em] text-black transition-colors hover:bg-[#FF3B30] hover:text-white disabled:opacity-70 md:w-auto"
              >
                {loading ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</>
                ) : done ? (
                  <><Check className="h-4 w-4" /> Received</>
                ) : (
                  <>Send the brief <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
